import { useState } from 'react'
import { Button } from '@merels/ui'
import { RefreshCw } from 'lucide-react'
import { TelemetryEvent } from './types'
import { LogLineRow } from './LogLineRow'

interface ProjectLogsViewProps {
  logs: TelemetryEvent[]
  loading: boolean
  onRefresh: () => void
}

export function ProjectLogsView({ logs, loading, onRefresh }: ProjectLogsViewProps) {
  const [filter, setFilter] = useState<'all' | 'creator' | 'runtime'>('all')

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48 text-muted-foreground">
        <div className="flex flex-col items-center space-y-3">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          <span>Fetching event timeline…</span>
        </div>
      </div>
    )
  }

  // Filter logs by category
  const filteredLogs = logs.filter(event => {
    const isRuntime = event.event_type === 'game_crash'
    if (filter === 'creator') return !isRuntime
    if (filter === 'runtime') return isRuntime
    return true
  })

  // Parse events and group by local date string
  const groupedEvents: { [dateStr: string]: TelemetryEvent[] } = {}
  
  filteredLogs.forEach(event => {
    const dateObj = new Date(event.timestamp)
    const dateStr = dateObj.toLocaleDateString(undefined, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
    
    if (!groupedEvents[dateStr]) {
      groupedEvents[dateStr] = []
    }
    groupedEvents[dateStr].push(event)
  })

  const dates = Object.keys(groupedEvents)

  // Collapse consecutive errors (game_crashes) in each group
  for (const dateStr of dates) {
    const rawEvents = groupedEvents[dateStr]
    const collapsed: Array<TelemetryEvent & { count?: number }> = []
    for (const event of rawEvents) {
      if (collapsed.length > 0) {
        const last = collapsed[collapsed.length - 1]
        if (last.event_type === 'game_crash' && event.event_type === 'game_crash') {
          last.count = (last.count || 1) + 1
          continue
        }
      }
      collapsed.push({ ...event })
    }
    groupedEvents[dateStr] = collapsed as any
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">
          Workspace Actions Timeline
        </span>
        <div className="flex items-center space-x-2">
          {/* Category Filters */}
          <div className="flex bg-[#12141d] p-0.5 rounded border border-[#1e212f]">
            <button
              onClick={() => setFilter('all')}
              className={`px-2 py-0.5 rounded-[3px] text-[9px] font-bold uppercase transition-all ${
                filter === 'all'
                  ? 'bg-[#1e212f] text-gray-200'
                  : 'text-muted-foreground hover:text-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('creator')}
              className={`px-2 py-0.5 rounded-[3px] text-[9px] font-bold uppercase transition-all ${
                filter === 'creator'
                  ? 'bg-[#1e212f] text-gray-200'
                  : 'text-muted-foreground hover:text-gray-200'
              }`}
            >
              Creator
            </button>
            <button
              onClick={() => setFilter('runtime')}
              className={`px-2 py-0.5 rounded-[3px] text-[9px] font-bold uppercase transition-all ${
                filter === 'runtime'
                  ? 'bg-[#1e212f] text-gray-200'
                  : 'text-muted-foreground hover:text-gray-200'
              }`}
            >
              Runtime
            </button>
          </div>
          <Button onClick={onRefresh} variant="secondary" className="h-7 w-7 p-0 flex items-center justify-center bg-[#141620] hover:bg-[#1f2231] border border-[#222535]">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {dates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-[#1e212f] p-8 text-center text-muted-foreground italic">
            No events logged for this project ID.
          </div>
        ) : (
          dates.map(dateStr => (
            <div key={dateStr} className="space-y-2">
              {/* Date Header Group */}
              <h4 className="text-[10px] font-bold text-gray-400 border-b border-[#1b1c26] pb-1 uppercase tracking-wider">
                {dateStr}
              </h4>
              
              <div className="space-y-1">
                {groupedEvents[dateStr].map(event => (
                  <LogLineRow key={event.id} event={event} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
